/* eslint-disable @repo/internal/react/require-jsdoc */
import React, { forwardRef, type ReactNode, type Ref } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, media, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

const rootStyles = xcss({
	display: 'block',
	position: 'relative',
	textDecoration: 'none',
	borderRadius: '12px',
	width: '100%',
	overflow: 'hidden',
	':hover': {
		textDecoration: 'none',
	},
});

const appearanceStyleMap = {
	yellow: xcss({
		backgroundColor: 'color.background.accent.orange.subtlest',
		borderStyle: 'solid',
		// @ts-ignore Needs to be replaced with an outline instead of border. However, outline doesn't work on active
		borderColor: token('color.background.accent.orange.subtlest'),
		borderWidth: '1px',
		':hover': {
			borderStyle: 'solid',
			borderColor: 'color.border.bold',
			borderWidth: '1px',
		},
		':active': {
			borderStyle: 'solid',
			borderColor: 'color.border.accent.orange',
			borderWidth: '1px',
		},
	}),
	purple: xcss({
		backgroundColor: 'color.background.accent.purple.subtlest',
		borderStyle: 'solid',
		// @ts-ignore Needs to be replaced with an outline instead of border. However, outline doesn't work on active
		borderColor: token('color.background.accent.purple.subtlest'),
		borderWidth: '1px',
		':hover': {
			borderStyle: 'solid',
			borderColor: 'color.border.bold',
			borderWidth: '1px',
		},
		':active': {
			borderStyle: 'solid',
			borderColor: 'color.border.accent.purple',
			borderWidth: '1px',
		},
	}),
	blue: xcss({
		backgroundColor: 'color.background.accent.blue.subtlest',
		borderStyle: 'solid',
		// @ts-ignore Needs to be replaced with an outline instead of border. However, outline doesn't work on active
		borderColor: token('color.background.accent.blue.subtlest'),
		borderWidth: '1px',
		':hover': {
			borderStyle: 'solid',
			borderColor: 'color.border.bold',
			borderWidth: '1px',
		},
		':active': {
			borderStyle: 'solid',
			borderColor: 'color.border.accent.blue',
			borderWidth: '1px',
		},
	}),
	gray: xcss({ backgroundColor: 'color.background.neutral' }),
};

const borderStyleMap = {
	'color.border': xcss({
		borderStyle: 'solid',
		borderWidth: '1px',
		borderColor: 'color.border',
		':hover': {
			borderColor: 'color.border.bold',
		},
		':active': {
			borderColor: 'color.border.brand',
		},
	}),
};

const externalIconStyles = xcss({
	position: 'absolute',
	insetBlockStart: 'space.200',
	insetInlineEnd: 'space.200',
	backgroundColor: 'elevation.surface',
	borderRadius: '4px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '24px',
	height: '24px',
	[media.above.sm]: {
		width: 'unset',
		height: 'unset',
	},
});

type CardProps = {
	appearance?: 'yellow' | 'purple' | 'blue' | 'gray';
	borderColor?: 'color.border';
	externalIconSpacing?: 'spacious';
	href: string;
	children: ReactNode;
};

export const Card = forwardRef(
	(
		{ appearance, borderColor, externalIconSpacing, href, children }: CardProps,
		ref: Ref<HTMLAnchorElement>,
	) => {
		return (
			<Anchor
				ref={ref}
				href={href}
				target={href.startsWith('http') ? '_blank' : undefined}
				xcss={[
					rootStyles,
					borderColor && borderStyleMap[borderColor],
					appearance && appearanceStyleMap[appearance],
				]}
			>
				{children}
				{href.startsWith('http') && (
					<Box xcss={externalIconStyles}>
						<LinkExternalIcon label="" color={token('color.icon')} spacing={externalIconSpacing} />
					</Box>
				)}
			</Anchor>
		);
	},
);
